import type {
  CourseAssignmentRuleCreateRequest,
  CourseAssignmentRuleExceptionCreateRequest,
  CourseAssignmentRuleExceptionResponse,
  CourseAssignmentRuleResponse,
  CourseAssignmentRuleUpdateRequest,
} from 'src/api/ProactApiClient';
import {courseAssignmentRuleApiClient} from 'src/api/ProactApiClientInstances';
import {emptySplitApi} from '../api';
import {clientQuery, voidClientQuery} from '../clientQuery';

export const courseAssignmentRuleApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getCourseAssignmentRulesByCourse: builder.query<
      CourseAssignmentRuleResponse[],
      number
    >({
      queryFn: clientQuery(courseDefinitionId =>
        courseAssignmentRuleApiClient.listByCourse(courseDefinitionId)
      ),
      providesTags: ['CourseAssignmentRules'],
    }),
    getCourseAssignmentRule: builder.query<CourseAssignmentRuleResponse, number>({
      queryFn: clientQuery(id => courseAssignmentRuleApiClient.get(id)),
      providesTags: ['CourseAssignmentRules'],
    }),
    createCourseAssignmentRule: builder.mutation<
      CourseAssignmentRuleResponse,
      CourseAssignmentRuleCreateRequest
    >({
      queryFn: clientQuery(request =>
        courseAssignmentRuleApiClient.create(request)
      ),
      invalidatesTags: ['CourseAssignmentRules'],
    }),
    updateCourseAssignmentRule: builder.mutation<
      CourseAssignmentRuleResponse,
      CourseAssignmentRuleUpdateRequest
    >({
      queryFn: clientQuery(request =>
        courseAssignmentRuleApiClient.update(request)
      ),
      invalidatesTags: ['CourseAssignmentRules'],
    }),
    deleteCourseAssignmentRule: builder.mutation<void, number>({
      queryFn: voidClientQuery(id => courseAssignmentRuleApiClient.delete(id)),
      invalidatesTags: ['CourseAssignmentRules', 'CourseAssignmentRuleExceptions'],
    }),
    listCourseAssignmentRuleExceptions: builder.query<
      CourseAssignmentRuleExceptionResponse[],
      number
    >({
      queryFn: clientQuery(ruleId =>
        courseAssignmentRuleApiClient.listExceptions(ruleId)
      ),
      providesTags: ['CourseAssignmentRuleExceptions'],
    }),
    addCourseAssignmentRuleException: builder.mutation<
      CourseAssignmentRuleExceptionResponse,
      CourseAssignmentRuleExceptionCreateRequest
    >({
      queryFn: clientQuery(request =>
        courseAssignmentRuleApiClient.addException(request)
      ),
      invalidatesTags: [
        'CourseAssignmentRuleExceptions',
        'CourseAssignmentRules',
      ],
    }),
    removeCourseAssignmentRuleException: builder.mutation<void, number>({
      queryFn: voidClientQuery(id =>
        courseAssignmentRuleApiClient.removeException(id)
      ),
      invalidatesTags: [
        'CourseAssignmentRuleExceptions',
        'CourseAssignmentRules',
      ],
    }),
    recalculateCourseAssignmentRules: builder.mutation<boolean, number>({
      queryFn: clientQuery(courseDefinitionId =>
        courseAssignmentRuleApiClient.recalculate(courseDefinitionId)
      ),
    }),
  }),
});

export const {
  useGetCourseAssignmentRulesByCourseQuery,
  useGetCourseAssignmentRuleQuery,
  useCreateCourseAssignmentRuleMutation,
  useUpdateCourseAssignmentRuleMutation,
  useDeleteCourseAssignmentRuleMutation,
  useListCourseAssignmentRuleExceptionsQuery,
  useAddCourseAssignmentRuleExceptionMutation,
  useRemoveCourseAssignmentRuleExceptionMutation,
  useRecalculateCourseAssignmentRulesMutation,
} = courseAssignmentRuleApi;
